import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { headerAssets, iframeConfig, statusOptions } from '../common/config';
import InfiniteScroll from 'react-infinite-scroll-component';
import Header from '../overlays/Header';
import StatusDropdown from '../dashboard/StatusDropdown';
import Spinner from '../common/spinner';

const WarningModal = ({ openModal = false, status, onClose }) => {
  if (!openModal) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white p-6 rounded-lg shadow-lg text-center">
        <h2 className="text-lg font-semibold mb-4">Status: {status}</h2>
        <p className="mb-4">
          This SKU is currently <strong>{status}</strong> and is not yet completed. Please wait until it is completed.
        </p>
        <button
          className="bg-spyne-blue text-white px-4 py-2 rounded"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
};

const DemoDashboard = () => {
  const [data, setData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const navigate = useNavigate();

  const fetchData = async (page) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL_API_SPYNE}/3d/api/v2/sku/all`, {
          params: { page_no: page, status: 'demo' }
        }
      );
      if (response?.data?.status === 'success') {
        const newData = response?.data?.data;
        setData((prevData) => (page > 1 ? [...prevData, ...newData] : newData));
        setHasMore(response.data.hasNextPage);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(pageNo);
  }, [pageNo]);

  const loadMoreData = () => {
    if (!loading && hasMore) {
      setPageNo((prevPage) => prevPage + 1);
    }
  };

  const handleCardClick = (sku_id, bg_id, status) => {
    if (status.toLowerCase() === 'completed' || status.toLowerCase() === 'demo')
      navigate(`/3d?sku_id=${sku_id}`);
    else {
      setOpenModal(true);
      setCurrentStatus(status);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    };
    return date.toLocaleDateString('en-US', options);
  };

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case "completed":
        return "text-green-600";
      case "in_progress":
        return "text-yellow-600";
      case "failed":
        return "text-red-600";
      case "yet_to_start":
        return "text-blue-400";
      default:
        return "text-gray-500";
    }
  };

  return (
    <>
      <Header />
      <main className='rounded-[16px] m-4 lg:my-6 lg:mx-12 bg-white p-2 lg:p-6 text-left'>
        <div className='border-[1.5px] border-blue-border rounded-[24px] bg-blue-background p-4 lg:py-12 lg:px-6 flex flex-col justify-center gap-4 md:gap-[30px]'>
          <div className='text-dark-blue'>
            <h1 className='font-bold text-2xl md:text-[32px] leading-[40px] text-center'>3D Journey</h1>
            <p className='text-center font-medium text-sm md:text-base'>Robust Immersive Experience</p>
          </div>
          <div>
            <InfiniteScroll
              dataLength={data.length}
              next={loadMoreData}
              hasMore={hasMore}
              loader={<div className='p-[13.5px] flex justify-center'><Spinner /></div>}
            >
              {data && data.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-[13.5px] p-2 md:p-[13.5px]">
                  {data.map((item) => {
                    const status = iframeConfig?.status[item?.status];
                    const source = iframeConfig?.source[item?.source];
                    const url = `${process.env.REACT_APP_3D_PREFIX_URL}/3d?sku_id=${item?.sku_id}`;

                    return (
                      <div
                        key={item.id}
                        className="bg-white cursor-pointer shadow-custom rounded-lg"
                        onClick={() => handleCardClick(item?.sku_id, item?.bg_id,'demo')}
                      >
                        <img src={item?.thumbnail || '/assets/images/sample_car.png'} className='rounded-t-lg w-full aspect-video object-cover object-center ' alt='car' />
                        <div className="px-4 py-2 leading-4 mt-2 flex flex-col gap-2 text-gray-title">
                          <h2 className="font-semibold text-base text-gray-title line-clamp-2 sm:line-clamp-1">{item?.name}</h2>
                          <div className="text-[14px] flex flex-col gap-2 w-full">
                            <p>SKU ID: {item?.sku_id}</p>
                            <p>Status: <span className={getStatusClass(item?.status)}>{status}</span></p>
                            <p>Source: {source}</p>
                            <p>Created On: {formatDate(item?.createdAt)}</p>
                            <p className="text-[#00000066] flex items-center">
                              <span className="line-clamp-1">
                                URL:
                                {status === 'completed' ? (
                                  <a href={url} className="break-all">{url}</a>
                                ) : (
                                  <span className='break-all'>{url}</span>
                                )}
                              </span>
                              <img src={headerAssets?.redirectIcon} className='h-4 text-[#00000066] ml-2' alt="redirect" />
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : !loading ? (
                <p className="text-center p-[13.5px]">
                  <span>No results</span>
                </p>
              ) : <></>}
            </InfiniteScroll>
            <WarningModal openModal={openModal} status={currentStatus} onClose={() => setOpenModal(false)} />
          </div>
        </div>
      </main>
    </>
  );
};

export default DemoDashboard;